import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { EmptyState } from "@/components/Shared";
import { fmtDate, fmtCur, fmtDateTime } from "@/lib/format";
import {
  Briefcase, Receipt, Mail, Users, Download, ExternalLink,
  AlertCircle, Building2, LogOut, Calendar, Filter, FileText,
  CheckCircle2, Clock, XCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "invoices", label: "Rechnungen",   icon: Receipt },
  { key: "mails",    label: "Buchhaltungs-Mails", icon: Mail },
  { key: "payroll",  label: "Lohnabrechnungen",  icon: Users },
];

const STATUS_META = {
  freigegeben: { label: "Freigegeben", color: "bg-blue-500/15 text-blue-300 border-blue-500/30", icon: Clock },
  bezahlt:     { label: "Bezahlt",     color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
  überfällig:  { label: "Überfällig",  color: "bg-red-500/15 text-red-300 border-red-500/30", icon: AlertCircle },
  teilbezahlt: { label: "Teilbezahlt", color: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Clock },
  storniert:   { label: "Storniert",   color: "bg-zinc-500/15 text-zinc-400 border-zinc-700", icon: XCircle },
};

export default function AccountantPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("invoices");

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (!user) return;
    if (!user.external_accountant_id) {
      // Not an accountant — redirect to normal app
      nav("/");
      return;
    }
    api.get("/accountant-portal/me").then(({ data }) => setProfile(data)).catch(() => {});
  }, [user, nav]);

  useEffect(() => {
    if (!profile?.is_accountant) return;
    api.get(`/accountant-portal/summary?days=${days}`).then(({ data }) => setSummary(data)).catch(() => {});
  }, [profile, days]);

  if (!user) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Lade…</div>;
  if (!profile) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Lade Portal…</div>;
  if (!profile.is_accountant) return null;

  const tenant = profile.tenant || {};
  const acc = profile.accountant || {};
  const branding = tenant.branding || {};

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="accountant-portal">
      {/* ============ Header ============ */}
      <header className="sticky top-0 z-30 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {branding.logo_url ? (
              <img src={`${BACKEND_URL}${branding.logo_url}`} alt={tenant.name}
                className="w-10 h-10 rounded object-contain bg-zinc-900 p-1 flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded bg-zinc-900 flex items-center justify-center flex-shrink-0" style={{ background: branding.primary_color || "#2563EB", opacity: 0.15 }}>
                <Building2 className="w-5 h-5" style={{ color: branding.primary_color || "#2563EB" }} />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Steuerberater-Portal</div>
              <h1 className="text-lg font-display font-semibold text-zinc-50 truncate">{tenant.name || "Plattform"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="hidden md:block text-right">
              <div className="text-xs text-zinc-300 font-medium">{user.name}</div>
              <div className="text-[10px] text-zinc-500 font-mono">{user.email}</div>
            </div>
            <button onClick={logout} data-testid="portal-logout"
              className="inline-flex items-center gap-1 px-3 py-2 text-xs bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-red-500/40 text-zinc-300 hover:text-red-300 rounded transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* ============ Welcome card ============ */}
        <div className="bg-gradient-to-br from-blue-500/5 via-zinc-900 to-zinc-900 border border-blue-500/20 rounded-md p-5 flex items-center gap-4 flex-wrap">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500/15 flex items-center justify-center">
            <Briefcase className="w-6 h-6 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-zinc-100 font-semibold">Willkommen, {acc.contact_first_name || user.name?.split(" ")[0] || "Berater"}</h2>
            <p className="text-xs text-zinc-400 mt-0.5">
              Hier finden Sie alle freigegebenen Buchhaltungs-Daten von {tenant.name} — Rechnungen, Personal-Mitteilungen und Lohnabrechnungen — der letzten {days} Tage.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
              data-testid="portal-days-filter"
              className="bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-blue-500">
              <option value={7}>Letzte 7 Tage</option>
              <option value={30}>Letzte 30 Tage</option>
              <option value={90}>Letzte 3 Monate</option>
              <option value={180}>Letzte 6 Monate</option>
              <option value={365}>Letzte 12 Monate</option>
            </select>
          </div>
        </div>

        {/* ============ KPI cards ============ */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="portal-summary">
            <Kpi icon={Receipt} label="Rechnungen" value={summary.invoices_count}
              hint={summary.invoices_overdue_count > 0 ? `${summary.invoices_overdue_count} überfällig` : "Alle aktuell"}
              hintColor={summary.invoices_overdue_count > 0 ? "text-red-300" : "text-emerald-300"}
              accent="#3b82f6" />
            <Kpi icon={Receipt} label="Brutto-Umsatz" value={fmtCur(summary.invoices_gross_sum)}
              hint={`Netto ${fmtCur(summary.invoices_net_sum)}`}
              accent="#10b981" />
            <Kpi icon={Mail} label="Buchhaltungs-Mails" value={summary.mails_count}
              hint="An-/Abmeldungen, Mitteilungen" accent="#a855f7" />
            <Kpi icon={Users} label="Aktive Mitarbeiter" value={summary.employees_active}
              hint="für Lohnabrechnung" accent="#f59e0b" />
          </div>
        )}

        {/* ============ Tabs ============ */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-md">
          <div className="flex border-b border-zinc-800 overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`portal-tab-${t.key}`}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors flex-shrink-0 ${active ? "text-blue-400 border-blue-500" : "text-zinc-400 border-transparent hover:text-zinc-200"}`}>
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
          <div className="p-5">
            {tab === "invoices" && <InvoicesTab days={days} />}
            {tab === "mails" && <MailsTab days={days} />}
            {tab === "payroll" && <PayrollTab />}
          </div>
        </div>

        <p className="text-center text-[10px] text-zinc-600 pt-4">
          Sie sind als <strong className="text-zinc-400">Steuerberater</strong> mit Lese-Zugriff angemeldet. Für Änderungen wenden Sie sich bitte an den Auftraggeber.
        </p>
      </main>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, hintColor, accent }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="text-2xl font-mono font-bold tabular-nums text-zinc-100">{value}</div>
      {hint && <div className={`text-[10px] mt-1 ${hintColor || "text-zinc-500"}`}>{hint}</div>}
    </div>
  );
}

function InvoicesTab({ days }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    const qs = statusFilter ? `&status=${statusFilter}` : "";
    api.get(`/accountant-portal/invoices?days=${days}${qs}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Konnte Rechnungen nicht laden"))
      .finally(() => setLoading(false));
  }, [days, statusFilter]);

  function downloadInvoice(inv) {
    api.post("/accountant-portal/track-download", {
      kind: "invoice", record_id: inv.id, label: inv.number,
    }).catch(() => {});
    const url = `${BACKEND_URL}/api/invoices/${inv.id}/pdf`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  }

  if (loading) return <div className="text-zinc-500 text-sm">Lade Rechnungen…</div>;
  if (items.length === 0) return <EmptyState icon={Receipt} text="Keine Rechnungen im gewählten Zeitraum." />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="text-xs text-zinc-500">{items.length} Einträge</span>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="invoices-status-filter"
          className="bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-blue-500">
          <option value="">Alle Status</option>
          <option value="freigegeben">Freigegeben</option>
          <option value="bezahlt">Bezahlt</option>
          <option value="teilbezahlt">Teilbezahlt</option>
          <option value="überfällig">Überfällig</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-3 py-2 text-left font-semibold">Nr.</th>
              <th className="px-3 py-2 text-left font-semibold">Kunde</th>
              <th className="px-3 py-2 text-left font-semibold">Datum</th>
              <th className="px-3 py-2 text-left font-semibold">Fällig</th>
              <th className="px-3 py-2 text-right font-semibold">Netto</th>
              <th className="px-3 py-2 text-right font-semibold">USt</th>
              <th className="px-3 py-2 text-right font-semibold">Brutto</th>
              <th className="px-3 py-2 text-center font-semibold">Status</th>
              <th className="px-3 py-2 text-right font-semibold w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const meta = STATUS_META[it.is_overdue ? "überfällig" : it.status] || STATUS_META.freigegeben;
              const StIcon = meta.icon;
              return (
                <tr key={it.id} data-testid={`invoice-row-${it.id}`}
                  className="border-b border-zinc-800/60 hover:bg-zinc-900/40">
                  <td className="px-3 py-2.5 font-mono text-xs text-zinc-100">{it.number}</td>
                  <td className="px-3 py-2.5 text-zinc-300 truncate max-w-xs">{it.customer_name || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400 font-mono text-xs">{fmtDate(it.issue_date)}</td>
                  <td className={`px-3 py-2.5 font-mono text-xs ${it.is_overdue ? "text-red-300" : "text-zinc-400"}`}>{fmtDate(it.due_date)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-zinc-200">{fmtCur(it.net_amount)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-zinc-500 text-xs">{it.vat_rate}%</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-zinc-100 font-semibold">{fmtCur(it.gross_amount)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}>
                      <StIcon className="w-3 h-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => downloadInvoice(it)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-zinc-800 hover:bg-blue-600 text-zinc-200 hover:text-white rounded transition-colors">
                      <Download className="w-3 h-3" /> PDF
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MailsTab({ days }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/accountant-portal/mails?days=${days}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Konnte Mails nicht laden"))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="text-zinc-500 text-sm">Lade Mails…</div>;
  if (items.length === 0) return <EmptyState icon={Mail} text="Keine Buchhaltungs-Mails im gewählten Zeitraum." />;

  const eventColor = { anmeldung: "emerald", abmeldung: "red", sonstige: "blue" };

  return (
    <div className="space-y-2">
      <div className="text-xs text-zinc-500 mb-2">{items.length} Einträge</div>
      {items.map((m) => {
        const c = eventColor[m.event] || "blue";
        return (
          <div key={m.id} data-testid={`mail-row-${m.id}`}
            className="bg-zinc-950 border border-zinc-800 rounded-md p-3 flex items-start gap-3 hover:border-zinc-700 transition-colors">
            <Mail className={`w-4 h-4 text-${c}-400 flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-${c}-500/15 text-${c}-300 border border-${c}-500/30`}>
                  {m.event === "anmeldung" ? "Anmeldung" : m.event === "abmeldung" ? "Abmeldung" : "Mitteilung"}
                </span>
                {m.employee_name && <span className="text-zinc-100 font-medium text-sm">{m.employee_name}</span>}
                {m.attachments_count > 0 && (
                  <span className="text-[10px] text-zinc-500 font-mono">📎 {m.attachments_count}</span>
                )}
              </div>
              <div className="text-zinc-300 text-sm mt-1 truncate">{m.subject}</div>
              <div className="text-[10px] text-zinc-600 font-mono mt-0.5">An: {m.to} · {fmtDateTime(m.created_at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PayrollTab() {
  const [data, setData] = useState({ items: [], available_months: [] });
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/accountant-portal/employees`)
      .then(({ data }) => {
        setData(data);
        if (data.available_months?.[0]) setMonth(data.available_months[0].label);
      })
      .catch(() => toast.error("Konnte Mitarbeiter nicht laden"))
      .finally(() => setLoading(false));
  }, []);

  function downloadPayslip(emp) {
    if (!month) { toast.error("Bitte Monat wählen"); return; }
    const [y, m] = month.split("-");
    api.post("/accountant-portal/track-download", {
      kind: "payslip", record_id: emp.id,
      label: `${emp.first_name} ${emp.last_name} · ${month}`,
    }).catch(() => {});
    const url = `${BACKEND_URL}/api/employees/${emp.id}/payslip?year=${y}&month=${parseInt(m)}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  }

  if (loading) return <div className="text-zinc-500 text-sm">Lade Mitarbeiter…</div>;
  if (data.items.length === 0) return <EmptyState icon={Users} text="Keine aktiven Mitarbeiter gefunden." />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="text-xs text-zinc-500">{data.items.length} aktive Mitarbeiter</span>
        <div className="inline-flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-zinc-500" />
          <select value={month} onChange={(e) => setMonth(e.target.value)}
            data-testid="payroll-month-filter"
            className="bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 font-mono">
            {data.available_months.map((m) => <option key={m.label} value={m.label}>{m.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {data.items.map((e) => (
          <div key={e.id} data-testid={`emp-row-${e.id}`}
            className="bg-zinc-950 border border-zinc-800 rounded-md p-3 flex items-center justify-between hover:border-zinc-700 transition-colors">
            <div className="min-w-0 flex-1">
              <div className="text-zinc-100 font-medium text-sm truncate">{e.first_name} {e.last_name}</div>
              <div className="text-[10px] text-zinc-500 font-mono">
                {e.personalnummer ? `Pers-Nr ${e.personalnummer} · ` : ""}{e.role || "—"}{e.department ? ` · ${e.department}` : ""}
              </div>
            </div>
            <button onClick={() => downloadPayslip(e)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-zinc-800 hover:bg-blue-600 text-zinc-200 hover:text-white rounded transition-colors flex-shrink-0">
              <FileText className="w-3 h-3" /> Lohnzettel
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
